export const memo = <I, O>(f: (i: I) => O) => {
    const cache = new Map<I, O>();
    return (input: I) => {
        const oldValue = cache.get(input);
        if (oldValue) {
            return oldValue;
        } else {
            const newValue = f(input);
            cache.set(input, newValue);
            return newValue;
        }
    };
};

export const reportChange = <T>() => {
    let cache: T | {} = {};
    return (value: T) => {
        const equal = isEqual(value, cache);
        cache = value;
        return equal;
    };
};

/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * https://github.com/facebook/react/blob/a9b035b0c2b8235405835beca0c4db2cc37f18d0/LICENSE
 *
 */

function is(x: any, y: any) {
    // eslint-disable-next-line no-self-compare
    return (x === y && (x !== 0 || 1 / x === 1 / y)) || (x !== x && y !== y);
}

/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.
 */
export function shallowEqual<T>(objA: T, objB: T): boolean {
    if (is(objA, objB)) {
        return true;
    }

    if (typeof objA !== "object" || objA === null || typeof objB !== "object" || objB === null) {
        return false;
    }

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
        return false;
    }

    // Test for A's keys different from B.
    for (let i = 0; i < keysA.length; i++) {
        if (
            !Object.prototype.hasOwnProperty.call(objB, keysA[i]) ||
            !is(objA[keysA[i]], objB[keysA[i]])
        ) {
            return false;
        }
    }

    return true;
}

export function isEqual<T>(objA: T, objB: T) {
    const eq = shallowEqual(objA, objB);
    if (!eq) {
        if (
            typeof objA === "object" &&
            objA !== null &&
            typeof objB === "object" &&
            objB !== null
        ) {
            const keysA = Object.keys(objA);
            const keysB = Object.keys(objB);

            if (keysA.length === keysB.length) {
                const newKeys = [] as string[];
                const oldKeys = [] as string[];
                const values = {};
                // Test for A's keys different from B.
                for (let i = 0; i < keysA.length; i++) {
                    if (
                        !Object.prototype.hasOwnProperty.call(objB, keysA[i]) ||
                        !is(objA[keysA[i]], objB[keysA[i]])
                    ) {
                        if (shallowEqual(objA[keysA[i]], objB[keysA[i]])) {
                            oldKeys.push(keysA[i]);
                        } else {
                            newKeys.push(keysA[i]);
                        }
                        values[keysA[i]] = { new: objA[keysA[i]], old: objB[keysA[i]] };
                    }
                }
                if (newKeys.length + oldKeys.length) {
                    return { eq, newKeys: newKeys.join(","), oldKeys: oldKeys.join(","), values };
                }
            }
        }
        return { eq, newValue: objA, oldValue: objB };
    }
    return { eq };
}
